import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError, GBP, fmtDate, fmtDateTime, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, UrgentTag, STATUS_META } from "@/components/StatusBadge";
import { StatusTimeline } from "@/components/StatusTimeline";
import { JOB_FLOW } from "@/components/StatusBadge";
import { toast } from "sonner";
import { ChevronLeft, FileText, Upload, Check, X, Download, Play, Info, AlertCircle, ShieldAlert } from "lucide-react";

function FileThumb({ file }) {
  const [url, setUrl] = useState(null);
  const isImg = (file.content_type || "").startsWith("image");
  useEffect(() => {
    let revoke;
    if (isImg) {
      api.get(`/files/${file.id}/download`, { responseType: "blob" }).then((r) => {
        const u = URL.createObjectURL(r.data); revoke = u; setUrl(u);
      }).catch(() => {});
    }
    return () => revoke && URL.revokeObjectURL(revoke);
  }, [file.id, isImg]);
  const open = async () => {
    try {
      const r = await api.get(`/files/${file.id}/download`, { responseType: "blob" });
      const u = URL.createObjectURL(r.data); window.open(u, "_blank");
    } catch { toast.error("Unable to open file"); }
  };
  return (
    <button onClick={open} data-testid={`file-${file.id}`} className="panel rounded-sm overflow-hidden text-left hover:border-[#C5A059] transition-colors group">
      <div className="aspect-square bg-[#1A1A1E] flex items-center justify-center overflow-hidden">
        {isImg && url ? <img src={url} alt={file.original_filename} className="w-full h-full object-cover" /> :
          <FileText size={28} className="text-[#5c5850]" />}
      </div>
      <div className="px-2 py-1.5 flex items-center justify-between gap-1">
        <span className="text-[0.65rem] text-[#8C867A] truncate">{file.original_filename}</span>
        <Download size={11} className="text-[#5c5850] group-hover:brass-text shrink-0" />
      </div>
    </button>
  );
}

export default function JobDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [costing, setCosting] = useState({ material_cost: 0, external_trade_cost: 0, internal_labour_cost: 0, other_cost: 0, selling_price: 0 });
  const [quote, setQuote] = useState({ amount: "", summary: "" });
  const [override, setOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [showDecline, setShowDecline] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [showInfo, setShowInfo] = useState(false);
  const [infoMsg, setInfoMsg] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/jobs/${id}`);
      setData(data);
      if (data.job.costing) setCosting(data.job.costing);
      if (data.job.quote) setQuote({ amount: data.job.quote.amount, summary: data.job.quote.summary || "" });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); navigate("/jobs"); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-[#8C867A]">Loading…</div>;
  const j = data.job;

  const changeStatus = async (status) => {
    try {
      await api.patch(`/jobs/${id}/status`, { status, override, reason: overrideReason });
      toast.success("Status updated"); setOverride(false); setOverrideReason(""); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); load(); }
  };
  const startJob = async () => {
    try { await api.post(`/jobs/${id}/start`); toast.success("Job started — in progress"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const requestInfo = async () => {
    if (!infoMsg.trim()) return toast.error("Describe what's needed");
    try { await api.post(`/jobs/${id}/request-info`, { message: infoMsg }); toast.success("Information requested"); setShowInfo(false); setInfoMsg(""); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const saveCosting = async () => {
    try { await api.post(`/jobs/${id}/costing`, { ...Object.fromEntries(Object.entries(costing).map(([k, v]) => [k, Number(v) || 0])) }); toast.success("Costing saved"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const issueQuote = async () => {
    if (!quote.amount) return toast.error("Enter a quote amount");
    try { await api.post(`/jobs/${id}/quote`, { amount: Number(quote.amount), summary: quote.summary }); toast.success("Quote issued"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const respondQuote = async (approve, reason = "") => {
    try {
      await api.post(`/jobs/${id}/quote/respond`, null, { params: { approve, ...(reason ? { reason } : {}) } });
      toast.success(approve ? "Quote approved" : "Quote declined"); setShowDecline(false); setDeclineReason(""); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const uploadFile = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("kind", file.type.startsWith("image") ? "IMAGE" : "DOCUMENT");
    try { await api.post(`/jobs/${id}/files`, fd); toast.success("File uploaded"); load(); }
    catch { toast.error("Upload failed"); }
  };

  const cost = costing;
  const totalCost = ["material_cost", "external_trade_cost", "internal_labour_cost", "other_cost"].reduce((s, k) => s + (Number(cost[k]) || 0), 0);
  const sell = Number(cost.selling_price) || 0;
  const gp = sell - totalCost;
  const gm = sell ? (gp / sell * 100) : 0;

  return (
    <div>
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-xs text-[#8C867A] hover:text-[#E2DFD7] mb-4"><ChevronLeft size={14} /> Back</button>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6 rise">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <span className="font-mono text-sm brass-text">{j.job_number}</span>
            {j.priority === "URGENT" && <UrgentTag />}
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl text-[#F5F3EF]">{j.title}</h1>
          <div className="text-sm text-[#8C867A] mt-1">{j.member_name} · {j.piece_type} · {j.metal}</div>
        </div>
        <StatusBadge status={j.status} />
      </div>

      {!isAdmin && (() => {
        let msg = null;
        if (j.info_requested) msg = `3B needs more information: ${j.info_request_message}`;
        else if (j.status === "QUOTED") msg = "A quote is ready — please review and approve or decline below.";
        else if (j.status === "READY FOR COLLECTION") msg = "Your piece is ready for collection.";
        else if (["SUBMITTED", "REVIEWING"].includes(j.status)) msg = "Submitted to 3B. We'll review and send a quote shortly.";
        else if (["APPROVED", "IN PROGRESS", "AWAITING EXTERNAL TRADE", "QUALITY CHECK"].includes(j.status)) msg = "Approved and in production. We'll notify you the moment it's ready.";
        else if (j.status === "COMPLETED") msg = "This job is complete. Thank you.";
        return msg ? (
          <div className="panel rounded-sm p-4 mb-4 flex items-start gap-3 rise" style={{ borderColor: "#3E382A" }} data-testid="member-next-action">
            <AlertCircle size={18} className="brass-text shrink-0 mt-0.5" />
            <div className="text-sm text-[#E2DFD7]">{msg}</div>
          </div>
        ) : null;
      })()}

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {/* Details */}
          <div className="panel rounded-sm p-5">
            <div className="eyebrow mb-3">Job Details</div>
            {j.description && <p className="text-sm text-[#C2BEB5] mb-4 leading-relaxed">{j.description}</p>}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
              {[["Client Ref", j.client_reference || "—"], ["Metal", j.metal || "—"], ["Weight", j.metal_weight || "—"],
                ["Stones", j.stone_info || "—"], ["Dimensions", j.dimensions || "—"], ["Quantity", j.quantity],
                ["Target", fmtDate(j.target_date)], ["Created", fmtDate(j.created_at)]].map(([l, v]) => (
                <div key={l}><div className="eyebrow mb-1">{l}</div><div className="text-[#E2DFD7]">{v}</div></div>
              ))}
            </div>
            <div className="mt-4"><div className="eyebrow mb-2">Services</div>
              <div className="flex flex-wrap gap-1.5">{(j.services || []).map((s) => <span key={s} className="text-[0.65rem] uppercase tracking-wider px-2 py-1 rounded-sm panel text-[#C2BEB5]">{s}</span>)}</div>
            </div>
          </div>

          {/* Member quote view */}
          {j.quote && (
            <div className="panel rounded-sm p-5" style={{ borderColor: "#3E382A" }}>
              <div className="flex items-center justify-between mb-2">
                <div className="eyebrow">3B Quote{j.quote.version ? ` · v${j.quote.version}` : ""}</div>
                <span className="text-[0.65rem] uppercase tracking-wider brass-text">{j.quote.status}</span>
              </div>
              <div className="text-3xl font-semibold brass-text mb-1 num">{GBP(j.quote.amount)}</div>
              {j.quote.summary && <p className="text-sm text-[#C2BEB5]">{j.quote.summary}</p>}
              {j.quote.decline_reason && <p className="text-xs mt-2" style={{ color: "#F59E0B" }}>Declined: {j.quote.decline_reason}</p>}
              {!isAdmin && j.status === "QUOTED" && !showDecline && (
                <div className="flex flex-wrap gap-2 mt-4">
                  <button onClick={() => respondQuote(true)} data-testid="approve-quote" className="btn-brass flex items-center gap-1.5 px-4 py-2.5 rounded-sm text-sm"><Check size={15} /> Approve Quote</button>
                  <button onClick={() => setShowDecline(true)} data-testid="decline-quote" className="panel px-4 py-2.5 rounded-sm text-sm text-[#C2BEB5] hover:border-[#F59E0B] transition-colors flex items-center gap-1.5"><X size={15} /> Decline</button>
                </div>
              )}
              {!isAdmin && showDecline && (
                <div className="mt-4 space-y-2">
                  <textarea value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} rows={2} data-testid="decline-reason"
                    className="w-full panel rounded-sm px-3 py-2 text-sm outline-none focus:border-[#C5A059]" placeholder="Optional: let 3B know why…" />
                  <div className="flex gap-2">
                    <button onClick={() => respondQuote(false, declineReason)} data-testid="confirm-decline" className="panel px-4 py-2 rounded-sm text-sm" style={{ color: "#F59E0B" }}>Confirm Decline</button>
                    <button onClick={() => setShowDecline(false)} className="text-xs text-[#8C867A] px-3">Cancel</button>
                  </div>
                </div>
              )}
              {j.quote_history && j.quote_history.length > 0 && (
                <div className="mt-4 pt-3 border-t hairline">
                  <div className="eyebrow mb-2">Previous Quotes</div>
                  {j.quote_history.map((qq, i) => (
                    <div key={i} className="flex items-center justify-between text-xs text-[#8C867A] py-0.5 num">
                      <span>v{qq.version || i + 1} · {GBP(qq.amount)}</span>
                      <span>{qq.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Files */}
          <div className="panel rounded-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="eyebrow">Files & Attachments</div>
              <label className="flex items-center gap-1.5 text-xs brass-text cursor-pointer link-underline">
                <Upload size={13} /> Upload
                <input type="file" className="hidden" onChange={uploadFile} data-testid="detail-file-input" />
              </label>
            </div>
            {data.files.length === 0 ? <div className="text-sm text-[#8C867A] py-4">No files attached.</div> :
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">{data.files.map((f) => <FileThumb key={f.id} file={f} />)}</div>}
          </div>

          {/* Admin costing */}
          {isAdmin && (
            <div className="panel rounded-sm p-5" style={{ borderColor: "#3E382A" }}>
              <div className="eyebrow mb-1">Internal Admin Costing</div>
              <div className="text-[0.68rem] text-[#8C867A] mb-4">Not visible to bench holders.</div>
              <div className="grid grid-cols-2 gap-3 mb-4">
                {[["material_cost", "Material Cost"], ["external_trade_cost", "External Trade"], ["internal_labour_cost", "Internal Labour"], ["other_cost", "Other Cost"], ["selling_price", "Selling Price"]].map(([k, l]) => (
                  <div key={k}>
                    <label className="eyebrow block mb-1.5">{l}</label>
                    <input type="number" step="0.01" data-testid={`cost-${k}`} value={cost[k]} onChange={(e) => setCosting((p) => ({ ...p, [k]: e.target.value }))}
                      className="w-full panel rounded-sm px-3 py-2 text-sm font-mono outline-none focus:border-[#C5A059]" />
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-3 mb-4 pt-4 border-t hairline">
                <div><div className="eyebrow mb-1">Total Cost</div><div className="font-mono text-[#E2DFD7]">{GBP(totalCost)}</div></div>
                <div><div className="eyebrow mb-1">Gross Profit</div><div className="font-mono" style={{ color: gp >= 0 ? "#34D399" : "#F59E0B" }}>{GBP(gp)}</div></div>
                <div><div className="eyebrow mb-1">Gross Margin</div><div className="font-mono brass-text">{gm.toFixed(1)}%</div></div>
              </div>
              <button onClick={saveCosting} data-testid="save-costing" className="btn-brass px-5 py-2.5 rounded-sm text-sm">Save Costing</button>
            </div>
          )}

          {/* Admin issue quote */}
          {isAdmin && (
            <div className="panel rounded-sm p-5">
              <div className="eyebrow mb-3">Member Quote</div>
              <div className="flex flex-wrap gap-3 items-end">
                <div className="w-40">
                  <label className="eyebrow block mb-1.5">Quote Amount (£)</label>
                  <input type="number" step="0.01" data-testid="quote-amount" value={quote.amount} onChange={(e) => setQuote((p) => ({ ...p, amount: e.target.value }))}
                    className="w-full panel rounded-sm px-3 py-2 text-sm font-mono outline-none focus:border-[#C5A059]" />
                </div>
                <div className="flex-1 min-w-[200px]">
                  <label className="eyebrow block mb-1.5">Summary</label>
                  <input value={quote.summary} onChange={(e) => setQuote((p) => ({ ...p, summary: e.target.value }))}
                    className="w-full panel rounded-sm px-3 py-2 text-sm outline-none focus:border-[#C5A059]" placeholder="What's included…" />
                </div>
                <button onClick={issueQuote} data-testid="issue-quote" className="btn-brass px-5 py-2.5 rounded-sm text-sm">Issue Quote</button>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {isAdmin && (
            <div className="panel rounded-sm p-5 space-y-3">
              {j.quote?.status === "APPROVED" && !["IN PROGRESS", "QUALITY CHECK", "READY FOR COLLECTION", "COMPLETED"].includes(j.status) && (
                <button onClick={startJob} data-testid="start-job" className="btn-brass w-full flex items-center justify-center gap-2 px-4 py-3 rounded-sm text-sm"><Play size={16} /> Start Job</button>
              )}
              <div>
                <div className="eyebrow mb-2">Update Status</div>
                <select data-testid="status-select" value={j.status} onChange={(e) => changeStatus(e.target.value)}
                  className="w-full panel rounded-sm px-3 py-2.5 text-sm outline-none focus:border-[#C5A059]">
                  {[...JOB_FLOW].map((s) => <option key={s} value={s}>{STATUS_META[s]?.label || s}</option>)}
                </select>
                <label className="flex items-center gap-2 mt-2.5 text-xs text-[#8C867A] cursor-pointer">
                  <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} data-testid="override-toggle" className="accent-[#C5A059]" />
                  <ShieldAlert size={13} /> Authorised override
                </label>
                {override && (
                  <input value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} data-testid="override-reason"
                    className="w-full panel rounded-sm px-3 py-2 text-xs mt-2 outline-none focus:border-[#C5A059]" placeholder="Reason (recorded in audit trail)" />
                )}
              </div>
              {!showInfo ? (
                <button onClick={() => setShowInfo(true)} data-testid="request-info-btn" className="w-full panel rounded-sm px-4 py-2.5 text-sm text-[#C2BEB5] hover:border-[#C5A059] transition-colors flex items-center justify-center gap-2"><Info size={15} /> Request Information</button>
              ) : (
                <div className="space-y-2">
                  <textarea value={infoMsg} onChange={(e) => setInfoMsg(e.target.value)} rows={2} data-testid="info-message" className="w-full panel rounded-sm px-3 py-2 text-sm outline-none focus:border-[#C5A059]" placeholder="What do you need from the member?" />
                  <div className="flex gap-2">
                    <button onClick={requestInfo} data-testid="send-info-request" className="btn-brass px-4 py-2 rounded-sm text-sm">Send Request</button>
                    <button onClick={() => setShowInfo(false)} className="text-xs text-[#8C867A] px-3">Cancel</button>
                  </div>
                </div>
              )}
              <div className="pt-3 border-t hairline">
                <div className="eyebrow mb-2">Job Readiness</div>
                <div className="space-y-1">
                  {[
                    ["Job title", !!j.title],
                    ["Services selected", (j.services || []).length > 0],
                    ["Description / brief", !!j.description],
                    ["Reference or files", data.files.length > 0 || !!j.client_reference],
                    ["Target date set", !!j.target_date],
                    ...(j.piece_type === "RING" ? [["Ring size / dimensions", !!j.dimensions]] : []),
                    ...((j.services || []).includes("STONE SETTING") ? [["Stone information", !!j.stone_info]] : []),
                    ...((j.services || []).includes("CASTING") ? [["Metal specified", !!j.metal]] : []),
                  ].map(([label, ok]) => (
                    <div key={label} className="flex items-center gap-2 text-xs">
                      {ok ? <Check size={13} style={{ color: "#34D399" }} /> : <AlertCircle size={13} style={{ color: "#F59E0B" }} />}
                      <span className={ok ? "text-[#8C867A]" : "text-[#E2DFD7]"}>{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="panel rounded-sm p-5">
            <div className="eyebrow mb-4">Progress</div>
            <StatusTimeline status={j.status} />
          </div>

          <div className="panel rounded-sm p-5">
            <div className="eyebrow mb-3">Audit Trail</div>
            <div className="space-y-3">
              {data.history.map((h) => (
                <div key={h.id} className="flex gap-2.5 text-sm">
                  <div className="w-1 rounded-full shrink-0 mt-1" style={{ background: "#3E382A" }} />
                  <div className="min-w-0">
                    <div className="text-[#E2DFD7]">{h.detail}</div>
                    <div className="text-[0.65rem] text-[#8C867A] mt-0.5">{h.by} · {fmtDateTime(h.at)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
